import { useState } from 'react';
import Navbar2 from '@/component/navbar2';
import Footer from '@/components/BrandFooter';
import { BRAND } from '@/brand';
import '../styles/publicPages.css';
import { L7_MEDIA } from '@/data/landingMedia';

// Accent — gold on corporate, blue on the consulting brand.
const GOLD = BRAND === 'corporate' ? '#FFD54F' : '#1976D2';
const GOLD_D = BRAND === 'corporate' ? '#E6B400' : '#1D4ED8';

const careersImg = L7_MEDIA.teamAtWork;
const womanImg   = L7_MEDIA.teamSession;
const car1 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/carr1.png';
const car2 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/carr2.png';
const car3 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/carr3.png';
const car4 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/carr4.png';
const valueImg = L7_MEDIA.deskOverhead;

const PERKS = [
  { icon: car1, bg: '#E6F4EC', title: 'Meaningful Work', desc: 'Help founders and SMEs in one of the world\'s fastest-growing markets. Every product you build lands in real boardrooms.' },
  { icon: car2, bg: '#E8F3FD', title: 'Remote-First Culture', desc: 'Work from anywhere in the UAE or globally. We care about output, not office hours.' },
  { icon: car3, bg: '#FFF8E1', title: 'Growth & Learning', desc: 'Dedicated learning budget, weekly team sessions, and a leadership ladder built for fast movers.' },
  { icon: car4, bg: '#E0F7FA', title: 'Equity & Benefits', desc: 'Competitive compensation, performance bonuses, comprehensive health coverage, and real ownership.' },
];

const DEPTS = [
  { title: 'Financial Advisory', desc: 'CFOs, accountants, and tax specialists who keep our clients compliant and investor-ready.' },
  { title: 'Product & Technology', desc: 'Engineers, designers, and PMs building the platform that powers our services.' },
  { title: 'Business Development', desc: 'Relationship builders who grow our client base and partner ecosystem across the region.' },
  { title: 'Client Success', desc: 'The team that ensures every startup and SME we work with thrives — not just survives.' },
  { title: 'Marketing & Brand', desc: 'Storytellers and growth specialists who take Conncct\'s mission to founders everywhere.' },
];

const VALUES = [
  { num: '01', title: 'Client First', desc: 'Every decision starts with one question: does this make our clients\' journey easier? We exist to serve them.' },
  { num: '02', title: 'Relentless Growth', desc: 'We don\'t settle. Individually and as a team, we push limits, embrace feedback, and level up every sprint.' },
  { num: '03', title: 'Radical Transparency', desc: 'No politics, no hidden agendas. We share context openly, debate ideas freely, and trust each other with hard truths.' },
  { num: '04', title: 'Ownership Mindset', desc: 'We hire people who treat Conncct like their own company — because we believe they should. Ship it, own it, improve it.' },
];

const STATS = [
  { n: '30+', l: 'Team members' },
  { n: '5+', l: 'Countries represented' },
  { n: '500+', l: 'Clients served' },
];

export default function Careers() {
  const [activeVal, setActiveVal] = useState<number | null>(null);

  return (
    <div className="pub-page">
      <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <Navbar2 logoTo="/" />
      </div>

      {/* ── HERO ── */}
      <section className="pub-hero cream" style={{ textAlign: 'center' }}>
        <div className="pub-inner narrow" style={{ position: 'relative', zIndex: 1 }}>
          <span className="pub-eye" style={{ marginBottom: 22, display: 'inline-flex' }}>We're Hiring</span>
          <h1 className="pub-h1" style={{ color: '#17263A', marginBottom: 24 }}>
            Build the future of<br />
            <span className="pub-hl">startup finance</span> in the UAE
          </h1>
          <p className="pub-lead" style={{ margin: '0 auto 36px', maxWidth: 600, textAlign: 'center' }}>
            Join a team of operators, advisors, and builders who believe that every ambitious founder deserves a world-class back office — and are building it.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://www.linkedin.com/company/conncct"
              className="pub-btn primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              View open roles →
            </a>
            <a href="mailto:careers@conncct.com" className="pub-btn ghost-light" style={{ borderColor: '#C0B090', color: '#42505F' }}>
              Send your CV
            </a>
          </div>
        </div>

        <div className="pub-inner wide" style={{ marginTop: 64 }}>
          <div style={{ borderRadius: 28, overflow: 'hidden', boxShadow: '0 40px 80px -30px rgba(23,38,58,.22)' }}>
            <img
              src={careersImg}
              alt="Life at Conncct"
              style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'cover' }}
            />
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="pub-sec navy" style={{ paddingTop: 72, paddingBottom: 72 }}>
        <div className="pub-inner">
          <div style={{ display: 'flex', justifyContent: 'center', gap: 80, flexWrap: 'wrap' }}>
            {STATS.map(({ n, l }) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 52, fontWeight: 800, letterSpacing: '-.04em', lineHeight: 1, color: GOLD }}>{n}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(206,219,242,.7)', marginTop: 6 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PERKS ── */}
      <section className="pub-sec white">
        <div className="pub-inner">
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <span className="pub-eye" style={{ marginBottom: 18, display: 'inline-flex' }}>Life at Conncct</span>
            <h2 className="pub-h2" style={{ color: '#17263A', marginTop: 0 }}>
              Why people choose to<br />stay and grow here
            </h2>
          </div>

          <div className="pub-grid-4">
            {PERKS.map(({ icon, bg, title, desc }) => (
              <div key={title} className="pub-perk">
                <div className="pub-tile" style={{ background: bg }}>
                  <img src={icon} alt="" />
                </div>
                <strong style={{ fontSize: 15, color: '#17263A', display: 'block' }}>{title}</strong>
                <p style={{ margin: 0, fontSize: 13.5, color: '#42505F', lineHeight: 1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OPEN ROLES ── */}
      <section className="pub-sec tinted">
        <div className="pub-inner">
          <div className="pub-split">
            <div className="pub-img" style={{ maxHeight: 560 }}>
              <img src={womanImg} alt="Conncct team member" style={{ objectPosition: 'top' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              <div>
                <span className="pub-eye blue" style={{ marginBottom: 18, display: 'inline-flex' }}>Departments</span>
                <h2 className="pub-h2" style={{ color: '#17263A', marginBottom: 14 }}>
                  Where you'll make your mark
                </h2>
                <p className="pub-lead">
                  We're a growing team with big ambitions. Wherever your skills lie, there's a seat at the table for the right person.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {DEPTS.map(({ title, desc }) => (
                  <div key={title} className="pub-dept">
                    <span className="pub-dept-dot" />
                    <div>
                      <strong style={{ fontSize: 14, color: '#17263A', display: 'block', marginBottom: 2 }}>{title}</strong>
                      <span style={{ fontSize: 13, color: '#42505F' }}>{desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <a
                  href="https://www.linkedin.com/company/conncct"
                  className="pub-btn blue"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See open positions →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ── */}
      <section className="pub-sec white">
        <div className="pub-inner">
          <div className="pub-split">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ marginBottom: 20 }}>
                <span className="pub-eye green" style={{ marginBottom: 18, display: 'inline-flex' }}>Our Values</span>
                <h2 className="pub-h2" style={{ color: '#17263A', marginBottom: 8 }}>
                  The principles we<br />hire and promote by
                </h2>
                <p className="pub-lead">These aren't posters on a wall. They're how we make decisions when it's hard.</p>
              </div>

              {VALUES.map(({ num, title, desc }, i) => (
                <button
                  key={num}
                  onClick={() => setActiveVal(activeVal === i ? null : i)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 18,
                    padding: '20px 0',
                    background: 'none',
                    border: 'none',
                    borderBottom: i < VALUES.length - 1 ? '1px solid #E8E2D2' : 'none',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: GOLD_D, flexShrink: 0, width: 24, paddingTop: 3, fontVariantNumeric: 'tabular-nums' }}>{num}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#17263A', marginBottom: activeVal === i ? 8 : 0, transition: 'margin .2s' }}>{title}</div>
                    {activeVal === i && (
                      <div style={{ fontSize: 14, color: '#42505F', lineHeight: 1.7 }}>{desc}</div>
                    )}
                  </div>
                  <span style={{ fontSize: 18, color: GOLD_D, flexShrink: 0, paddingTop: 1, fontWeight: 300, transition: 'transform .2s', transform: activeVal === i ? 'rotate(45deg)' : 'none' }}>+</span>
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={valueImg}
                alt="Our values"
                style={{ width: '100%', maxWidth: 420, height: 'auto', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="pub-sec cream">
        <div className="pub-inner">
          <div className="pub-cta">
            <div className="pub-cta-glow" />
            <div className="pub-split" style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <h2 className="pub-h2" style={{ color: '#fff' }}>
                  Think you'd fit in?<br />We'd love to hear from you.
                </h2>
                <p className="pub-lead light">
                  We review every application. If there isn't a role listed that fits, send us your CV and tell us how you'd add value.
                </p>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <a
                    href="https://www.linkedin.com/company/conncct"
                    className="pub-btn gold"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View open roles →
                  </a>
                  <a href="mailto:careers@conncct.com" className="pub-btn ghost-light">
                    Email us directly
                  </a>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  { n: '30+', l: 'Team members' },
                  { n: '5+', l: 'Countries' },
                  { n: '500+', l: 'Clients served' },
                  { n: '100%', l: 'Remote-friendly' },
                ].map(({ n, l }) => (
                  <div key={l} style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 16, padding: '20px 22px', textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.04em', lineHeight: 1, color: GOLD }}>{n}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(206,219,242,.65)', marginTop: 5 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
